import type { TemplateResult } from '@design.estate/dees-element';
import type { TDiffViewRequest } from '../00group-dataview/dees-dataview-codebox/dees-dataview-codebox.js';
import type { IDiffStats } from '../00group-dataview/dees-dataview-codebox/codebox.diff.js';
import type { IHarnessToolCall, THarnessContentBlock } from './interfaces.js';
import {
  harnessFileWriteDiffStats,
  harnessIsRecord,
  harnessSummaryLine,
  harnessTerminalResultSummary,
} from './harness.helpers.js';

/**
 * Render helpers handed to tool body renderers. Implemented by
 * dees-harness-tool-card (which owns the templates); typed here so custom
 * descriptors can be authored against a stable surface.
 */
export interface IHarnessToolRenderHelpers {
  /** highlighted code block (dees-dataview-codebox) with optional filename */
  codeBlock(code: string, language?: string, filename?: string): TemplateResult;
  /** before/after diff (dees-dataview-codebox diff mode) */
  diffBlock(before: string, after: string, options?: {
    language?: string;
    filename?: string;
    view?: TDiffViewRequest;
    unifiedDiff?: string;
    showLineNumbers?: boolean;
  }): TemplateResult;
  /** plain monospace block with truncation + expand */
  monoBlock(text: string, options?: { maxChars?: number; tone?: 'default' | 'error' }): TemplateResult;
  /** two-column key/value summary */
  keyValues(pairs: Array<[string, string]>): TemplateResult;
  /** MCP content blocks (dees-harness-content-blocks) */
  contentBlocks(blocks: THarnessContentBlock[]): TemplateResult;
  /** constrained inline image from base64 */
  image(dataBase64: string, mimeType: string, alt?: string): TemplateResult;
}

export interface IHarnessToolDescriptor {
  /** stable kind id, e.g. 'terminal', 'file-read', 'mcp', 'unknown' */
  kind: string;
  /** exact raw-name matches (fast path); lowercased on registration */
  names?: string[];
  /** fallback matcher when no name matched */
  match?: (call: IHarnessToolCall) => boolean;
  label: string | ((call: IHarnessToolCall) => string);
  /** lucide icon spec, e.g. 'lucide:Terminal' */
  icon: string;
  subtitle?: (call: IHarnessToolCall) => string;
  /**
   * Custom body renderer. Built-in kinds leave this unset — the tool card
   * ships body templates for them — so descriptors stay DOM-free unless a
   * consumer wants a bespoke body.
   */
  renderBody?: (call: IHarnessToolCall, helpers: IHarnessToolRenderHelpers) => TemplateResult;
  /** One-line outcome shown in the header while the card is collapsed. */
  summary?: (call: IHarnessToolCall) => string;
  /**
   * Exact added/removed line counts for the row's diff stat. Returns undefined when the call's
   * input does not allow an exact count, so the row shows nothing rather than a guess. Evaluated
   * once per call object, not per render.
   */
  diffStats?: (call: IHarnessToolCall) => IDiffStats | undefined;
  /** styles the card with a destructive accent regardless of status */
  destructive?: boolean;
}

/** Splits smartagent's MCP namespacing convention `server__tool`. */
export const parseMcpName = (
  rawName: string,
): { serverName: string; toolName: string } | undefined => {
  const separatorIndex = rawName.indexOf('__');
  if (separatorIndex <= 0 || separatorIndex >= rawName.length - 2) return undefined;
  return {
    serverName: rawName.slice(0, separatorIndex),
    toolName: rawName.slice(separatorIndex + 2),
  };
};

const inputField = (call: IHarnessToolCall, key: string): string => {
  return harnessIsRecord(call.input) && typeof call.input[key] === 'string'
    ? (call.input[key] as string)
    : '';
};

const inputAny = (call: IHarnessToolCall, key: string): unknown => {
  return harnessIsRecord(call.input) ? call.input[key] : undefined;
};

const filePathOf = (call: IHarnessToolCall): string =>
  inputField(call, 'path') || inputField(call, 'filePath') || inputField(call, 'file');

const projectTaskActions = new Set(['list', 'create', 'update', 'delete', 'clear']);

const taskCallKind = (call: IHarnessToolCall): 'subtask' | 'project-task' | undefined => {
  if (String(call.name || '').toLowerCase() !== 'task') return undefined;
  if (call.childSessionId || call.subtask) return 'subtask';
  return projectTaskActions.has(inputField(call, 'action')) ? 'project-task' : 'subtask';
};

const defaultDescriptors: IHarnessToolDescriptor[] = [
  {
    kind: 'terminal',
    names: ['run_command', 'run_shell', 'bash', 'shell', 'exec', 'command'],
    label: 'Shell',
    icon: 'lucide:Terminal',
    subtitle: (call) => {
      const command = inputField(call, 'command');
      const cwd = inputField(call, 'cwd') || inputField(call, 'workdir');
      return command ? `${command}${cwd ? ` — in ${cwd}` : ''}` : harnessSummaryLine(call.input);
    },
    summary: harnessTerminalResultSummary,
  },
  {
    kind: 'file-read',
    names: ['read_file', 'read'],
    label: 'Read',
    icon: 'lucide:FileText',
    subtitle: (call) => {
      const path = filePathOf(call);
      const startLine = inputAny(call, 'startLine');
      const endLine = inputAny(call, 'endLine');
      const range = startLine || endLine ? `:${startLine ?? ''}-${endLine ?? ''}` : '';
      return path ? `${path}${range}` : harnessSummaryLine(call.input);
    },
  },
  {
    kind: 'file-write',
    names: ['write_file', 'write', 'edit', 'edit_file', 'apply_patch', 'patch', 'file_change', 'filechange'],
    label: 'Write',
    icon: 'lucide:FilePen',
    subtitle: (call) => {
      const path = filePathOf(call);
      if (path) return path;
      const changes = harnessIsRecord(call.input) ? call.input.changes : undefined;
      if (Array.isArray(changes)) {
        const paths = changes.filter(harnessIsRecord).map((change) => change.path)
          .filter((value): value is string => typeof value === 'string');
        if (paths.length) return paths.join(', ');
      }
      const patchText = harnessIsRecord(call.input) ? call.input.patchText : undefined;
      if (typeof patchText === 'string') {
        const match = patchText.match(/^\*\*\* (?:Add|Update|Delete) File: (.+)$/m);
        if (match) return match[1].trim();
      }
      return harnessSummaryLine(call.input);
    },
    diffStats: harnessFileWriteDiffStats,
  },
  {
    kind: 'file-delete',
    names: ['delete_file', 'delete', 'remove_file'],
    label: 'Delete',
    icon: 'lucide:Trash2',
    subtitle: (call) => filePathOf(call) || harnessSummaryLine(call.input),
    destructive: true,
  },
  {
    kind: 'dir-list',
    names: ['list_directory', 'list', 'ls'],
    label: 'List',
    icon: 'lucide:FolderOpen',
    subtitle: (call) => {
      const path = filePathOf(call) || '.';
      return inputAny(call, 'recursive') ? `${path} (recursive)` : path;
    },
  },
  {
    kind: 'browser',
    names: ['browser'],
    label: 'Browser',
    icon: 'lucide:Globe',
    subtitle: (call) => {
      const action = inputField(call, 'action') || 'snapshot';
      const target = inputField(call, 'url') || inputField(call, 'selector');
      return `${action}${target ? ` ${target}` : ''}`;
    },
  },
  {
    kind: 'http',
    names: ['http_get', 'http_post', 'webfetch', 'fetch'],
    label: (call) => (call.name === 'http_post' ? 'HTTP POST' : call.name === 'http_get' ? 'HTTP GET' : 'Fetch'),
    icon: 'lucide:ArrowDownUp',
    subtitle: (call) => inputField(call, 'url') || harnessSummaryLine(call.input),
  },
  {
    kind: 'json',
    names: ['json_validate', 'json_transform'],
    label: (call) => (call.name === 'json_transform' ? 'JSON Transform' : 'JSON Validate'),
    icon: 'lucide:Braces',
    subtitle: (call) => {
      const source = inputField(call, 'jsonString');
      return source ? (source.length > 60 ? `${source.slice(0, 60)}…` : source) : harnessSummaryLine(call.input);
    },
  },
  {
    kind: 'search',
    names: ['grep', 'glob', 'websearch', 'search'],
    label: 'Search',
    icon: 'lucide:Search',
    subtitle: (call) =>
      inputField(call, 'pattern') || inputField(call, 'query') || filePathOf(call) || harnessSummaryLine(call.input),
  },
  {
    kind: 'subtask',
    names: ['delegate', 'subagent'],
    match: (call) => taskCallKind(call) === 'subtask',
    label: 'Subagent',
    icon: 'lucide:Bot',
    // The child's model belongs in the collapsed header: a subagent often
    // runs on a different model than the chat that spawned it.
    subtitle: (call) => {
      const description = inputField(call, 'description')
        || inputField(call, 'prompt')
        || harnessSummaryLine(call.input);
      return call.model ? `${description} — ${call.model}` : description;
    },
  },
  {
    kind: 'question',
    names: ['askuserquestion', 'ask_user_question', 'ask_question', 'ask_user', 'question'],
    label: 'Question',
    icon: 'lucide:MessageCircleQuestionMark',
    subtitle: (call) => inputField(call, 'question') || inputField(call, 'prompt') || harnessSummaryLine(call.input),
  },
  {
    kind: 'project-task',
    match: (call) => taskCallKind(call) === 'project-task',
    label: 'Project task',
    icon: 'lucide:ListChecks',
    subtitle: (call) => {
      const action = inputField(call, 'action');
      return action ? `Action: ${action}` : harnessSummaryLine(call.input);
    },
  },
  {
    kind: 'todo',
    names: ['todowrite', 'todo_write', 'todoread'],
    label: 'Todos',
    icon: 'lucide:ListTodo',
    subtitle: (call) => {
      const todos = inputAny(call, 'todos');
      return Array.isArray(todos) ? `${todos.length} todos` : harnessSummaryLine(call.input);
    },
  },
  {
    kind: 'mcp',
    label: (call) => call.mcp?.annotations?.title || call.mcp?.toolName || parseMcpName(call.name)?.toolName || call.name,
    icon: 'lucide:Plug',
    match: (call) => Boolean(call.mcp) || Boolean(parseMcpName(call.name)),
    subtitle: (call) => harnessSummaryLine(call.input),
  },
  {
    kind: 'unknown',
    label: (call) => call.name || 'Tool',
    icon: 'lucide:Wrench',
    subtitle: (call) => harnessSummaryLine(call.input),
  },
];

/**
 * Maps raw tool calls to render descriptors. A pre-seeded singleton covers
 * the built-in and MCP conventions; apps register custom tools once at boot
 * (`DeesHarnessToolRegistry.default.register(...)`) or hand an isolated
 * `fork()` to individual elements via their `.toolRegistry` property.
 */
export class DeesHarnessToolRegistry {
  public static readonly default: DeesHarnessToolRegistry = new DeesHarnessToolRegistry(defaultDescriptors);

  private descriptors: IHarnessToolDescriptor[] = [];
  private byName = new Map<string, IHarnessToolDescriptor>();
  private byKind = new Map<string, IHarnessToolDescriptor>();
  private registryRevision = 0;

  public get revision(): number {
    return this.registryRevision;
  }

  constructor(seedDescriptors: IHarnessToolDescriptor[] = []) {
    for (const descriptor of seedDescriptors) {
      this.register(descriptor);
    }
  }

  /** Registers (or replaces, by kind) a descriptor. Later wins on names. */
  public register(descriptor: IHarnessToolDescriptor): this {
    const existing = this.byKind.get(descriptor.kind);
    if (existing) {
      this.descriptors = this.descriptors.filter((candidate) => candidate !== existing);
      for (const [name, mapped] of this.byName) {
        if (mapped === existing) this.byName.delete(name);
      }
    }
    this.byKind.set(descriptor.kind, descriptor);
    this.descriptors.push(descriptor);
    for (const name of descriptor.names ?? []) {
      this.byName.set(name.toLowerCase(), descriptor);
    }
    this.registryRevision += 1;
    return this;
  }

  public unregister(kind: string): this {
    const existing = this.byKind.get(kind);
    if (!existing) return this;
    this.byKind.delete(kind);
    this.descriptors = this.descriptors.filter((candidate) => candidate !== existing);
    for (const [name, mapped] of this.byName) {
      if (mapped === existing) this.byName.delete(name);
    }
    this.registryRevision += 1;
    return this;
  }

  public descriptorForKind(kind: string): IHarnessToolDescriptor | undefined {
    return this.byKind.get(kind);
  }

  /** Never returns undefined — falls back to the 'unknown' descriptor. */
  public resolve(call: IHarnessToolCall): IHarnessToolDescriptor {
    if (call.kind) {
      const explicit = this.byKind.get(call.kind);
      if (explicit) return explicit;
    }
    const named = this.byName.get(String(call.name || '').toLowerCase());
    if (named) return named;
    for (const descriptor of this.descriptors) {
      if (descriptor.match?.(call)) return descriptor;
    }
    return this.byKind.get('unknown') ?? defaultDescriptors[defaultDescriptors.length - 1];
  }

  public resolveKind(name: string): string {
    return this.resolve({ id: '', name, status: 'completed' }).kind;
  }

  /** Isolated copy for demos, tests, or per-pane toolsets. */
  public fork(): DeesHarnessToolRegistry {
    return new DeesHarnessToolRegistry(this.descriptors.map((descriptor) => ({ ...descriptor })));
  }
}
